// apps/web/src/features/call-center/components/CallCenterLeadsHeader.tsx
//
// Header strip for the external agent's own-leads screen (#1398): title on the
// left, a "New lead" button + a Filters toggle on the right. The phone-search
// Input now lives inside a collapsible Filters panel below the header row.
// The search value feeds the `phone` query param (debounced upstream in the page).
import { useTranslation } from 'react-i18next';
import {
  Button,
  FilterPanel,
  FilterToggleButton,
  Input,
  ToolbarActions,
  useFilterDisclosure,
} from '../../../ui/index.js';

// WHY: the stored phone is `VarChar(32)`; the query schema caps the filter at
//   32 chars, so cap the control here too.
const SEARCH_MAX_CHARS = 32;

export function CallCenterLeadsHeader({
  narrow,
  search,
  onSearchChange,
  onNewLead,
}: {
  narrow: boolean;
  search: string;
  onSearchChange: (next: string) => void;
  onNewLead: () => void;
}) {
  const { t } = useTranslation('callCenter');
  const { open, toggle, panelId } = useFilterDisclosure();
  const filtersActiveCount = search.trim().length > 0 ? 1 : 0;
  return (
    <>
      <div
        style={{
          padding: '14px 24px',
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          alignItems: narrow ? 'stretch' : 'center',
          gap: narrow ? 10 : 16,
          borderBottom: '1px solid var(--border-1)',
          flex: 'none',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: 'var(--fg-1)' }}>
          {t('selfService.title')}
        </h1>
        {!narrow && <div style={{ flex: 1 }} />}
        {/* WHY: the toggle + create button stay on one row — `flexWrap: 'nowrap'`
            on narrow prevents two compact buttons from awkwardly wrapping to a
            second row. Desktop keeps the default wrap. */}
        <ToolbarActions style={{ gap: narrow ? 8 : 12, flexWrap: narrow ? 'nowrap' : 'wrap' }}>
          <FilterToggleButton
            open={open}
            activeCount={filtersActiveCount}
            label={t('selfService.filters')}
            size={narrow ? 'md' : 'sm'}
            aria-controls={panelId}
            onClick={toggle}
            data-testid="cc-leads-filters-toggle"
          />
          <Button kind="primary" icon="plus" data-testid="cc-leads-new" onClick={onNewLead}>
            {t('selfService.newLead')}
          </Button>
        </ToolbarActions>
      </div>
      <FilterPanel open={open} id={panelId}>
        <div style={{ width: narrow ? '100%' : 240 }}>
          <Input
            icon="search"
            placeholder={t('selfService.searchPlaceholder')}
            value={search}
            onChange={onSearchChange}
            maxLength={SEARCH_MAX_CHARS}
            ariaLabel={t('selfService.searchPlaceholder')}
          />
        </div>
      </FilterPanel>
    </>
  );
}
